import { DRAGONBONES_PATH } from 'Constants/Dragonbones';
import { getEventOfferProductId } from 'State/Events/EventHelper';
import { IGameState } from 'State/Game/IGameState';
import { IAPStateHelper } from 'State/Player/IAP/IAPStateHelper';
import { getHighestLevelCompleted } from 'State/Player/PlayerLevelHelper';
import { EventType } from 'Utility/Helpers/Events/EventType';
import { IEventOfferData } from 'Utility/Helpers/IAP/IEventOfferData';
import { OfferType } from 'Utility/Helpers/IAP/Offers/OfferTypes/OfferType';
import { PopupOffersOfType, PopupOfferType } from 'Utility/Helpers/IAP/PopupOffers';
import { ProductId } from 'Utility/Product';
import { areDBResourcesLoaded, addDragonbonesResources } from 'Utility/Resource/ResourceHelper';

export const EVENT_OFFERS: Record<keyof PopupOffersOfType<PopupOfferType.EVENT_OFFER>, IEventOfferData> = {
    [EventType.XMAS_2021]: {
        condition: (state: IGameState): boolean => {
            const highestLevel = getHighestLevelCompleted(state.playerState);
            const productId = getEventOfferProductId(state, EventType.XMAS_2021);
            return highestLevel >= 22 && !IAPStateHelper.hasPurchasedProduct(state, productId);
        },
        queueAssets: () => {
            if (!areDBResourcesLoaded('christmas_offer')) {
                addDragonbonesResources('christmas_offer', `${DRAGONBONES_PATH}/ChristmasOffer/`);
            }
        },
        buttonCacheKey: 'christmas_map_icon',
        productId: [ProductId.XMAS_2021_OFFER],
        offer: {
            tiers: [{ type: OfferType.SINGLE, productId: ProductId.XMAS_2021_OFFER }],
        },
    },
    [EventType.EASTER_2022]: {
        condition: (state: IGameState): boolean => {
            const highestLevel = getHighestLevelCompleted(state.playerState);
            const productId = getEventOfferProductId(state, EventType.EASTER_2022);
            return highestLevel >= 22 && !IAPStateHelper.hasPurchasedProduct(state, productId);
        },
        queueAssets: () => {
            if (!areDBResourcesLoaded('easter_offer')) {
                addDragonbonesResources('easter_offer', `${DRAGONBONES_PATH}/Easter2022Offer/`);
            }
        },
        buttonCacheKey: 'easter_offer_icon',
        productId: [ProductId.EASTER_2022_OFFER],
        offer: {
            tiers: [{ type: OfferType.SINGLE, productId: ProductId.EASTER_2022_OFFER }],
        },
    },
    [EventType.JUL4_2022]: {
        condition: (state: IGameState): boolean => {
            const highestLevel = getHighestLevelCompleted(state.playerState);
            const productId = getEventOfferProductId(state, EventType.JUL4_2022);
            return highestLevel >= 22 && !IAPStateHelper.hasPurchasedProduct(state, productId);
        },
        queueAssets: () => {
            if (!areDBResourcesLoaded('4july_offer')) {
                addDragonbonesResources('4july_offer', `${DRAGONBONES_PATH}/Events/Jul42022/Offer/`);
            }
        },
        buttonCacheKey: '4_july_map_icon',
        productId: [ProductId.JUL4_2022_OFFER],
        offer: {
            tiers: [{ type: OfferType.SINGLE, productId: ProductId.JUL4_2022_OFFER }],
        },
    },
    [EventType.HALLOWEEN_2022]: {
        condition: (state: IGameState): boolean => {
            const highestLevel = getHighestLevelCompleted(state.playerState);
            const productId = getEventOfferProductId(state, EventType.HALLOWEEN_2022);
            return highestLevel >= 22 && !IAPStateHelper.hasPurchasedProduct(state, productId);
        },
        queueAssets: () => {
            if (!areDBResourcesLoaded('halloween_offer')) {
                addDragonbonesResources('halloween_offer', `${DRAGONBONES_PATH}/Events/Halloween2022/Offer/`);
            }
        },
        buttonCacheKey: 'halloween_offer_map_icon',
        productId: [ProductId.HALLOWEEN_2022_OFFER],
        offer: {
            tiers: [{ type: OfferType.SINGLE, productId: ProductId.HALLOWEEN_2022_OFFER }],
        },
    },
    [EventType.XMAS_2022]: {
        condition: (state: IGameState): boolean => {
            const highestLevel = getHighestLevelCompleted(state.playerState);
            const productId = getEventOfferProductId(state, EventType.XMAS_2022);
            return productId && highestLevel >= 22 && !IAPStateHelper.hasPurchasedProduct(state, productId);
        },
        queueAssets: () => {
            if (!areDBResourcesLoaded('christmas_offer')) {
                addDragonbonesResources('christmas_offer', `${DRAGONBONES_PATH}/ChristmasOffer/`);
            }
        },
        buttonCacheKey: 'christmas_map_icon',
        productId: [ProductId.XMAS_2022_OFFER_1, ProductId.XMAS_2022_OFFER_2],
        offer: {
            tiers: [
                { type: OfferType.SINGLE, productId: ProductId.XMAS_2022_OFFER_1 },
                { type: OfferType.SINGLE, productId: ProductId.XMAS_2022_OFFER_2 },
            ],
        },
    },
    [EventType.VALENTINES_2023]: {
        condition: (state: IGameState): boolean => {
            const highestLevel = getHighestLevelCompleted(state.playerState);
            const hasPurchasedFinal = [
                ProductId.VALENTINES_2023_v1_3,
                ProductId.VALENTINES_2023_v2_3,
                ProductId.VALENTINES_2023_v3_3,
            ].some(productId => IAPStateHelper.hasPurchasedProduct(state, productId));
            return highestLevel >= 22 && !hasPurchasedFinal;
        },
        queueAssets: () => {
            if (!areDBResourcesLoaded('valentines_day_offer')) {
                addDragonbonesResources('valentines_day_offer', `${DRAGONBONES_PATH}/Events/Valentines2023/`);
            }
            if (!areDBResourcesLoaded('valentines_gift_exp')) {
                addDragonbonesResources('valentines_gift_exp', `${DRAGONBONES_PATH}/Events/Valentines2023/`);
            }
        },
        buttonCacheKey: 'valentines_offer_icon',
        productId: [],
        offer: {
            tiers: [
                {
                    type: OfferType.MULTI,
                    requirements: { max: 0 },
                    products: [
                        { productId: ProductId.VALENTINES_2023_v1_1 },
                        { productId: ProductId.VALENTINES_2023_v1_2 },
                        { productId: ProductId.VALENTINES_2023_v1_3 },
                    ],
                },
                {
                    type: OfferType.MULTI,
                    requirements: { max: 249.99 },
                    products: [
                        { productId: ProductId.VALENTINES_2023_v2_1 },
                        { productId: ProductId.VALENTINES_2023_v2_2 },
                        { productId: ProductId.VALENTINES_2023_v2_3 },
                    ],
                },
                {
                    type: OfferType.MULTI,
                    products: [
                        { productId: ProductId.VALENTINES_2023_v3_1 },
                        { productId: ProductId.VALENTINES_2023_v3_2 },
                        { productId: ProductId.VALENTINES_2023_v3_3 },
                    ],
                },
            ],
        },
    },
    [EventType.BLACKFRIDAY_2023]: {
        condition: (state: IGameState): boolean => {
            const highestLevel = getHighestLevelCompleted(state.playerState);
            const hasPurchasedFinal = [
                ProductId.BLACKFRIDAY_2023_v1_3,
                ProductId.BLACKFRIDAY_2023_v2_3,
                ProductId.BLACKFRIDAY_2023_v3_3,
            ].some(productId => IAPStateHelper.hasPurchasedProduct(state, productId));
            return highestLevel >= 22 && !hasPurchasedFinal;
        },
        queueAssets: () => {
            if (!areDBResourcesLoaded('black_friday_offer')) {
                addDragonbonesResources('black_friday_offer', `${DRAGONBONES_PATH}/Events/BlackFriday2023/`);
            }
            if (!areDBResourcesLoaded('black_friday_gift_exp')) {
                addDragonbonesResources('black_friday_gift_exp', `${DRAGONBONES_PATH}/Events/BlackFriday2023/`);
            }
        },
        buttonCacheKey: 'blackfriday_offer_icon',
        productId: [],
        offer: {
            tiers: [
                {
                    type: OfferType.MULTI,
                    requirements: { max: 0 },
                    products: [
                        { productId: ProductId.BLACKFRIDAY_2023_v1_1 },
                        { productId: ProductId.BLACKFRIDAY_2023_v1_2 },
                        { productId: ProductId.BLACKFRIDAY_2023_v1_3 },
                    ],
                },
                {
                    type: OfferType.MULTI,
                    requirements: { max: 249.99 },
                    products: [
                        { productId: ProductId.BLACKFRIDAY_2023_v2_1 },
                        { productId: ProductId.BLACKFRIDAY_2023_v2_2 },
                        { productId: ProductId.BLACKFRIDAY_2023_v2_3 },
                    ],
                },
                {
                    type: OfferType.MULTI,
                    products: [
                        { productId: ProductId.BLACKFRIDAY_2023_v3_1 },
                        { productId: ProductId.BLACKFRIDAY_2023_v3_2 },
                        { productId: ProductId.BLACKFRIDAY_2023_v3_3 },
                    ],
                },
            ],
        },
    },
    [EventType.XMAS_2023]: {
        condition: (state: IGameState): boolean => {
            const highestLevel = getHighestLevelCompleted(state.playerState);
            const productId = getEventOfferProductId(state, EventType.XMAS_2023);
            return productId && highestLevel >= 22 && !IAPStateHelper.hasPurchasedProduct(state, productId);
        },
        queueAssets: () => {
            if (!areDBResourcesLoaded('christmas_offer_2023')) {
                addDragonbonesResources('christmas_offer_2023', `${DRAGONBONES_PATH}/ChristmasOffer2023/`);
            }
        },
        buttonCacheKey: 'christmas_map_icon',
        productId: [ProductId.XMAS_2023_v1, ProductId.XMAS_2023_v2, ProductId.XMAS_2023_v3],
        offer: {
            tiers: [
                { type: OfferType.SINGLE, requirements: { max: 0 }, productId: ProductId.XMAS_2023_v1 },
                { type: OfferType.SINGLE, requirements: { max: 249.99 }, productId: ProductId.XMAS_2023_v2 },
                { type: OfferType.SINGLE, productId: ProductId.XMAS_2023_v3 },
            ],
        },
    },
    [EventType.VALENTINES_2024]: {
        condition: (state: IGameState): boolean => {
            const highestLevel = getHighestLevelCompleted(state.playerState);
            const hasPurchasedFinal = [
                ProductId.VALENTINES_2024_v1_3,
                ProductId.VALENTINES_2024_v2_3,
                ProductId.VALENTINES_2024_v3_3,
            ].some(productId => IAPStateHelper.hasPurchasedProduct(state, productId));
            return highestLevel >= 22 && !hasPurchasedFinal;
        },
        queueAssets: () => {
            if (!areDBResourcesLoaded('valentines_day_offer')) {
                addDragonbonesResources('valentines_day_offer', `${DRAGONBONES_PATH}/Events/Valentines2023/`);
            }
            if (!areDBResourcesLoaded('valentines_gift_exp')) {
                addDragonbonesResources('valentines_gift_exp', `${DRAGONBONES_PATH}/Events/Valentines2023/`);
            }
        },
        buttonCacheKey: 'valentines_offer_icon',
        productId: [],
        offer: {
            tiers: [
                {
                    type: OfferType.MULTI,
                    requirements: { max: 0 },
                    products: [
                        { productId: ProductId.VALENTINES_2024_v1_1 },
                        { productId: ProductId.VALENTINES_2024_v1_2 },
                        { productId: ProductId.VALENTINES_2024_v1_3 },
                    ],
                },
                {
                    type: OfferType.MULTI,
                    requirements: { max: 200 },
                    products: [
                        { productId: ProductId.VALENTINES_2024_v2_1 },
                        { productId: ProductId.VALENTINES_2024_v2_2 },
                        { productId: ProductId.VALENTINES_2024_v2_3 },
                    ],
                },
                {
                    type: OfferType.MULTI,
                    products: [
                        { productId: ProductId.VALENTINES_2024_v3_1 },
                        { productId: ProductId.VALENTINES_2024_v3_2 },
                        { productId: ProductId.VALENTINES_2024_v3_3 },
                    ],
                },
            ],
        },
    },
    [EventType.EASTER_2024]: {
        condition: (state: IGameState): boolean => {
            const highestLevel = getHighestLevelCompleted(state.playerState);
            const productId = getEventOfferProductId(state, EventType.EASTER_2024);
            return productId && highestLevel >= 22 && !IAPStateHelper.hasPurchasedProduct(state, productId);
        },
        queueAssets: () => {
            if (!areDBResourcesLoaded('easter_offer')) {
                addDragonbonesResources('easter_offer', `${DRAGONBONES_PATH}/Easter2022Offer/`);
            }
        },
        buttonCacheKey: 'easter_offer_icon',
        productId: [ProductId.EASTER_2024_v1, ProductId.EASTER_2024_v2, ProductId.EASTER_2024_v3],
        offer: {
            tiers: [
                { type: OfferType.SINGLE, requirements: { max: 0 }, productId: ProductId.EASTER_2024_v1 },
                { type: OfferType.SINGLE, requirements: { max: 149.99 }, productId: ProductId.EASTER_2024_v2 },
                { type: OfferType.SINGLE, productId: ProductId.EASTER_2024_v3 },
            ],
        },
    },
};
